import type React from 'react';
import type {RootState} from 'store/types';
import {
  getFirstManifestationPublishYear,
  selectMetadataEditMetadataExpressionById,
  selectMetadataEditMetadataManifestationsByExpressionId,
  selectMetadataEditSavedMetadataExpressionById,
  useCreateSelectorById,
} from 'services/metadata';
import {useAppSelector} from 'store/hooks';
import {useExpressionTitle} from './hooks/useExpressionTitle';

type Props = {
  expressionId: string;
};

const selectFirstManifestationPublishYearByExpressionId =
  (expressionId: string) => (state: RootState) =>
    getFirstManifestationPublishYear(
      selectMetadataEditMetadataManifestationsByExpressionId(expressionId)(
        state,
      ) ?? [],
    );

/**
 * Live expression title, which updates publishedYear based on typed input in all publishedYears
 * (expression override and each of the manifestations)
 */
export const ExpressionTitle: React.FC<Props> = ({expressionId}) => {
  // Stable expression from backend
  const expressionSelector = useCreateSelectorById(
    expressionId,
    selectMetadataEditSavedMetadataExpressionById,
  );
  const expression = useAppSelector(expressionSelector);

  // Edited expression
  const expressionEditSelector = useCreateSelectorById(
    expressionId,
    selectMetadataEditMetadataExpressionById,
  );
  const expressionEdit = useAppSelector(expressionEditSelector);

  // PublishedYear based on edited manifestations
  const firstManifestationPublishedYearSelector = useCreateSelectorById(
    expressionId,
    selectFirstManifestationPublishYearByExpressionId,
  );
  const firstManifestationPublishedYear = useAppSelector(
    firstManifestationPublishedYearSelector,
  );

  return useExpressionTitle(
    expression,
    expressionEdit,
    firstManifestationPublishedYear,
  );
};
